import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { AppLayout } from "../components/AppLayout";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { Settings2, Save, RotateCcw, Loader2, PartyPopper } from "lucide-react";

const COLS = [["urbana_singola", "Urbana Singola €"], ["urbana_ar", "Urbana A/R € (inclusi 90 min attesa)"], ["km_extra", "Extra urbana €/km"], ["secondo_paziente", "Secondo paziente €"]];

export default function TariffePage() {
  const [t, setT] = useState(null);
  const [te, setTe] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savingEv, setSavingEv] = useState(false);
  const load = () => { api.get("/amministrazione/tariffe").then((r) => setT(r.data)); api.get("/amministrazione/tariffe-eventi").then((r) => setTe(r.data)); };
  const saveEv = async () => {
    setSavingEv(true);
    try {
      await api.put("/amministrazione/tariffe-eventi", { risorse: Object.fromEntries(Object.entries(te.risorse).map(([k, v]) => [k, num(v)])) });
      toast.success("Tariffe eventi salvate"); load();
    } catch (err) { toast.error("Non salvate", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
    finally { setSavingEv(false); }
  };
  useEffect(() => { load(); }, []);

  const setM = (m, k, v) => setT((prev) => ({ ...prev, mezzi: { ...prev.mezzi, [m]: { ...prev.mezzi[m], [k]: v } } }));
  const num = (v) => (v === "" || v == null ? 0 : Number(String(v).replace(",", ".")));
  const save = async () => {
    setSaving(true);
    try {
      const body = {
        mezzi: Object.fromEntries(Object.entries(t.mezzi).map(([m, vals]) => [m, { ...vals, ...Object.fromEntries(COLS.map(([k]) => [k, num(vals[k])])) }])),
        medico_ora: num(t.medico_ora), infermiere_ora: num(t.infermiere_ora),
        franchigia_singola_ore: num(t.franchigia_singola_ore ?? 1), franchigia_ar_ore: num(t.franchigia_ar_ore ?? 1.5),
      };
      await api.put("/amministrazione/tariffe", body);
      toast.success("Tariffe salvate: i nuovi preventivi useranno questi valori");
      load();
    } catch (err) { toast.error("Non salvate", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
    finally { setSaving(false); }
  };
  const reset = async () => {
    if (!window.confirm("Ripristinare le tariffe iniziali della Delibera XI/6645/2017?")) return;
    await api.post("/amministrazione/tariffe/reset"); toast.success("Tariffe ripristinate"); load();
  };

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto nx-fade-in pb-10">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
          <div>
            <h1 className="text-3xl font-bold font-heading text-slate-900 tracking-tight mb-1 flex items-center gap-2"><Settings2 className="h-7 w-7 text-primary" /> Configurazione Tariffe</h1>
            <p className="text-sm text-slate-500">Tariffe applicate ai nuovi preventivi (Delibera N. XI/6645/2017). {t?.updated_at && <>Ultimo aggiornamento {t.updated_at.slice(0, 10).split("-").reverse().join("/")} · {t.updated_by}</>}</p>
          </div>
          <div className="flex gap-2">
            <Button data-testid="tariffe-reset" variant="outline" className="gap-2" onClick={reset}><RotateCcw className="h-4 w-4" /> Ripristina default</Button>
            <Button data-testid="tariffe-save" className="gap-2" onClick={save} disabled={saving || !t}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva tariffe</Button>
          </div>
        </div>
        {!t ? <Skeleton className="h-64 w-full" /> : (
          <div className="space-y-6">
            <Card className="border-slate-200 overflow-hidden">
              <Table>
                <TableHeader><TableRow><TableHead>Tipo di mezzo</TableHead>{COLS.map(([k, l]) => <TableHead key={k} className="text-right">{l}</TableHead>)}</TableRow></TableHeader>
                <TableBody>
                  {Object.entries(t.labels.mezzi).map(([m, label]) => (
                    <TableRow key={m}>
                      <TableCell className="font-semibold text-slate-800">{label}</TableCell>
                      {COLS.map(([k]) => (
                        <TableCell key={k} className="text-right">
                          <Input data-testid={`tariffa-${m}-${k}`} type="number" step="0.01" min="0" value={t.mezzi[m]?.[k] ?? 0} onChange={(e) => setM(m, k, e.target.value)} className="h-8 w-28 ml-auto text-right" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
            <Card className="border-slate-200 p-5 grid sm:grid-cols-2 gap-4">
              {[["medico_ora", "Medico €/ora"], ["infermiere_ora", "Infermiere €/ora"]].map(([k, l]) => (
                <div key={k}><Label className="nx-label">{l}</Label><Input data-testid={`tariffa-${k}`} type="number" step="0.01" min="0" value={t[k] ?? 0} onChange={(e) => setT({ ...t, [k]: e.target.value })} className="mt-1.5" /></div>
              ))}
            </Card>

            <div className="flex flex-wrap items-end justify-between gap-3 pt-4">
              <div>
                <h2 className="text-xl font-bold font-heading text-slate-900 flex items-center gap-2"><PartyPopper className="h-5 w-5 text-primary" /> Tariffe Eventi (€/ora per unità)</h2>
                <p className="text-sm text-slate-500">Usate nei preventivi evento: unità × tariffa oraria × ore di servizio. {te?.updated_at && <>Aggiornate il {te.updated_at.slice(0, 10).split("-").reverse().join("/")} · {te.updated_by}</>}</p>
              </div>
              <Button data-testid="tariffe-eventi-save" className="gap-2" onClick={saveEv} disabled={savingEv || !te}>{savingEv ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva tariffe eventi</Button>
            </div>
            {!te ? <Skeleton className="h-32 w-full" /> : (
              <Card className="border-slate-200 p-5 grid sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="tariffe-eventi-card">
                {Object.entries(te.labels).map(([k, l]) => (
                  <div key={k}><Label className="nx-label">{l} €/ora</Label><Input data-testid={`tariffa-evento-${k}`} type="number" step="0.01" min="0" value={te.risorse[k] ?? 0} onChange={(e) => setTe({ ...te, risorse: { ...te.risorse, [k]: e.target.value } })} className="mt-1.5" /></div>
                ))}
              </Card>
            )}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
